import { QueryKey, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  BlogCommentEntity,
  ServerListResponse,
  ServerSingleResponse,
} from "../../types";
import { AxiosResponse } from "axios";
import { reactionIdsToEmoji } from "../../components/reactions/ReactionsPopover";

const UPVOTE_REACTION_ID = 9;
const DOWNVOTE_REACTION_ID = 10;

type CommentPostPromise = Promise<
  AxiosResponse<ServerSingleResponse<BlogCommentEntity>>
>;
type UseEditCommentsonfigProps = (data: {
  reactionTypeId: reactionIdsToEmoji;
  comment: BlogCommentEntity;
}) => CommentPostPromise;

export function useCommentReactions(
  queryKey: QueryKey,
  onToggleCommentReaction: UseEditCommentsonfigProps,
) {
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: ({
      reactionTypeId,
      comment,
    }: {
      reactionTypeId: reactionIdsToEmoji;
      comment: BlogCommentEntity;
    }) =>
      onToggleCommentReaction({
        reactionTypeId,
        comment,
      }),

    onMutate: ({ comment, reactionTypeId }) => {
      const prevState = queryClient.getQueryData(queryKey) as AxiosResponse<
        ServerListResponse<BlogCommentEntity>
      >;
      const prevReleaseCommentsData = prevState.data.payload.results;
      const newComment: BlogCommentEntity = {
        ...comment,
      };

      if (reactionTypeId === UPVOTE_REACTION_ID) {
        if (newComment.user_vote_reaction_type_id === DOWNVOTE_REACTION_ID) {
          newComment.total_bad -= 1;
        }
        if (newComment.user_vote_reaction_type_id === reactionTypeId) {
          newComment.user_vote_reaction_type_id = null;
          newComment.total_good -= 1;
        } else {
          newComment.user_vote_reaction_type_id = reactionTypeId;
          newComment.total_good += 1;
        }
      } else if (reactionTypeId === DOWNVOTE_REACTION_ID) {
        if (newComment.user_vote_reaction_type_id === UPVOTE_REACTION_ID) {
          newComment.total_good -= 1;
        }
        if (newComment.user_vote_reaction_type_id === reactionTypeId) {
          newComment.user_vote_reaction_type_id = null;
          newComment.total_bad -= 1;
        } else {
          newComment.user_vote_reaction_type_id = reactionTypeId;
          newComment.total_bad += 1;
        }
      }

      const newState = {
        ...prevState,
        data: {
          payload: {
            results: reactToComment(prevReleaseCommentsData, newComment),
          },
        },
      };

      queryClient.setQueryData(queryKey, newState);

      return newState;
    },
    onError: (_, __, context) => {
      queryClient.setQueryData(queryKey, context);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return mutate;
}

function reactToComment(
  comments: BlogCommentEntity[],
  updatedComment: BlogCommentEntity,
): BlogCommentEntity[] {
  return comments.map((comment) => {
    if (comment.id === updatedComment.id) {
      return { ...comment, ...updatedComment };
    }

    if (comment.replies?.length) {
      return {
        ...comment,
        replies: reactToComment(comment.replies, updatedComment),
      };
    }

    return comment;
  });
}
